import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// ============================================================
// CRITICAL: Clear corrupted IndexedDB cache BEFORE React mounts
// This prevents race conditions where React Query tries to hydrate
// with corrupted cache data before cleanup can occur.
// ============================================================
const APP_VERSION = '2.1.0'; // Must match cacheManager.ts
const LAST_VERSION_KEY = 'alacart-last-app-version';

/**
 * CRITICAL: Async function that WAITS for IndexedDB deletion to complete
 * before allowing React to mount. This prevents the race condition where
 * React Query hydrates corrupted cache data.
 */
const clearCorruptedCacheSync = (): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
      
      if (lastVersion !== APP_VERSION) {
        const deleteRequest = indexedDB.deleteDatabase('alacart-query-cache');
        
        deleteRequest.onsuccess = () => {
          if (import.meta.env.DEV) {
            console.log('[Main] IndexedDB cache cleared on version change:', lastVersion, '→', APP_VERSION);
          }
          localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
          resolve();
        };
        
        deleteRequest.onerror = () => {
          if (import.meta.env.DEV) {
            console.warn('[Main] Failed to clear IndexedDB cache');
          }
          localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
          resolve();
        };
        
        deleteRequest.onblocked = () => {
          if (import.meta.env.DEV) {
            console.warn('[Main] IndexedDB deletion blocked - forcing resolve');
          }
          localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
          resolve();
        };
        
        // Safety timeout - 3 seconds max to prevent infinite hang
        setTimeout(() => {
          if (import.meta.env.DEV) {
            console.warn('[Main] IndexedDB cleanup timeout - proceeding with mount');
          }
          localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
          resolve();
        }, 3000);
      } else {
        resolve();
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn('[Main] Error during pre-mount cache cleanup:', e);
      }
      resolve();
    }
  });
};

// WAIT for cache cleanup to complete BEFORE mounting React
// This is the key fix - React Query will only hydrate AFTER cache is clean
clearCorruptedCacheSync().then(() => {
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
